import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "antd";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import DataParser from "../../../utlits/Dataparser";
import LeadSettingsService from "../../../Services/LeadSettingsService";
import SocketService from "../../../Services/SocketService";
import TableSkeleton from "../../Reuseable/TableSkeleton";

const LeadSettings = () => {
    const companyId = DataParser.CompanyId();

    const [settingsList, setSettingsList] = useState([]);
    const [isLoading, setIsLoading]       = useState(false);
    const [submitting, setSubmitting]     = useState(false);
    const [autoCloseDays, setAutoCloseDays] = useState("");
    const [editID, setEditID]             = useState(null);
    const [error, setError]               = useState("");

    const getSettings = () => {
        if (!companyId) return;
        setIsLoading(true);
        LeadSettingsService.getLeadSettings(companyId)
            .then((res) => {
                if (res.Good) setSettingsList(res.data || []);
            })
            .catch(() => {})
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        getSettings();
    }, [companyId]);

    useEffect(() => {
        SocketService.connect();
        const handleUpdate = (data) => {
            if (Number(data.company_id) === Number(companyId)) getSettings();
        };
        SocketService.on("lead_settings_update", handleUpdate);
        return () => SocketService.off("lead_settings_update", handleUpdate);
    }, [companyId]);

    const clearState = () => {
        setEditID(null);
        setAutoCloseDays("");
        setError("");
    };

    const validate = () => {
        if (!autoCloseDays || Number(autoCloseDays) < 1) {
            setError("Please enter a valid number of days (minimum 1)");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            const res = await LeadSettingsService.createLeadSetting({
                company_id:      companyId,
                auto_close_days: parseInt(autoCloseDays),
            });
            if (res.Good) {
                toast.success("Lead setting created successfully!");
                document.getElementById("LeadSettingsCreateClose")?.click();
                getSettings();
                clearState();
            } else {
                toast.error(res.data || "Action failed");
            }
        } catch (err) {
            toast.error(err.response?.data?.data || "Process failed");
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            const res = await LeadSettingsService.updateLeadSetting(editID, {
                auto_close_days: parseInt(autoCloseDays),
            });
            if (res.Good) {
                toast.success("Lead setting updated successfully!");
                document.getElementById("LeadSettingsEditClose")?.click();
                getSettings();
                clearState();
            } else {
                toast.error(res.data || "Action failed");
            }
        } catch (err) {
            toast.error(err.response?.data?.data || "Process failed");
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleStatus = async (item) => {
        try {
            const res = await LeadSettingsService.updateStatus(item.id);
            if (res.Good) {
                toast.success("Status updated");
                getSettings();
            }
        } catch {
            toast.error("Status update failed");
        }
    };

    const handleOpenEdit = (e, item) => {
        e.preventDefault();
        setEditID(item.id);
        setAutoCloseDays(item.auto_close_days);
        setError("");
    };

    return (
        <>
            <Helmet>
                <title>Lead Settings - Planit</title>
            </Helmet>
            <div className="main-content">
                <div className="page-content Pagebody">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div className="page-title-box d-flex align-items-center justify-content-end">
                                    <div className="page-title-right">
                                        <ol className="breadcrumb" style={{ marginBottom: "10px !important" }}>
                                            <li className="breadcrumb-item">
                                                <Link to={`/${DataParser.CompanyIdentifierRole()}/dashboard`}>Home</Link>
                                            </li>
                                            <li className="breadcrumb-item">
                                                <Link to={`/${DataParser.CompanyIdentifierRole()}/settings`}>Settings</Link>
                                            </li>
                                            <li className="breadcrumb-item active">Lead Settings</li>
                                        </ol>
                                        {settingsList.length === 0 && (
                                            <button
                                                className="btn btn-primary waves-effect waves-light"
                                                style={{ float: "right" }}
                                                data-bs-toggle="modal"
                                                data-bs-target="#createLeadSettingModal"
                                            >
                                                Configure
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-12">
                                <div className="card" style={{ backgroundColor: "transparent", boxShadow: "none" }}>
                                    <div className="card-body p-0">
                                        <div className="table-responsive">
                                            <table className="table mb-0 table-striped custom-table">
                                                <thead>
                                                    <tr className="tableHeaderbg text-center">
                                                        <th className="text-center">Sr#</th>
                                                        <th className="text-center">Auto-Close After (Days)</th>
                                                        <th className="text-center">Status</th>
                                                        <th className="text-center">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {isLoading ? (
                                                        <TableSkeleton cols={4} rows={6} />
                                                    ) : settingsList.length > 0 ? (
                                                        settingsList.map((item, index) => (
                                                            <tr key={item.id} className="text-center">
                                                                <td>{index + 1}</td>
                                                                <td>
                                                                    <span className="fw-semibold">{item.auto_close_days}</span> days of inactivity
                                                                </td>
                                                                <td>
                                                                    <button
                                                                        className={`btn btn-sm ${item.status === 1 ? "btn-success" : "btn-danger"}`}
                                                                        onClick={() => handleToggleStatus(item)}
                                                                    >
                                                                        {item.status === 1 ? "Active" : "Inactive"}
                                                                    </button>
                                                                </td>
                                                                <td className="tableset">
                                                                    <Link
                                                                        to="#"
                                                                        onClick={(e) => handleOpenEdit(e, item)}
                                                                        data-bs-toggle="modal"
                                                                        data-bs-target="#editLeadSettingModal"
                                                                    >
                                                                        <Tooltip title="Edit">
                                                                            <i className="uil-pen iconscolor" />
                                                                        </Tooltip>
                                                                    </Link>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="4" className="text-center">
                                                                No configuration found. Click <strong>Configure</strong> to set the auto-close days.
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Create Modal */}
                <div id="createLeadSettingModal" className="modal fade" tabIndex={-1} role="dialog"
                    aria-labelledby="createLeadSettingLabel" aria-hidden="true" data-bs-keyboard="false">
                    <div className="modal-dialog modal-dialog-centered">
                        <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title" id="createLeadSettingLabel">Configure Lead Auto-Close</h5>
                                    <button type="button" className="btn-close" id="LeadSettingsCreateClose"
                                        data-bs-dismiss="modal" aria-label="Close" onClick={clearState} />
                                </div>
                                <div className="modal-body">
                                    <p className="text-muted" style={{ fontSize: 13 }}>
                                        Leads with no activity for the specified number of days will be automatically marked as <strong>Lost</strong>.
                                    </p>
                                    <div className="col-12 mb-3 d-flex align-items-center">
                                        <div className="col-md-5">
                                            <label className="form-label mb-0">Auto-Close After (Days)*</label>
                                        </div>
                                        <div className="col-md-7">
                                            <input
                                                type="number"
                                                className={`form-control ${error ? "is-invalid" : ""}`}
                                                placeholder="e.g. 30"
                                                value={autoCloseDays}
                                                min="1"
                                                onChange={(e) => { setAutoCloseDays(e.target.value); setError(""); }}
                                            />
                                            {error && <small className="text-danger">{error}</small>}
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-light waves-effect"
                                        data-bs-dismiss="modal" onClick={clearState}>Close</button>
                                    <button type="submit" className="btn btn-primary waves-effect waves-light"
                                        disabled={submitting}>
                                        {submitting ? "Saving..." : "Save"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Edit Modal */}
                <div id="editLeadSettingModal" className="modal fade" tabIndex={-1} role="dialog"
                    aria-labelledby="editLeadSettingLabel" aria-hidden="true" data-bs-keyboard="false">
                    <div className="modal-dialog modal-dialog-centered">
                        <form onSubmit={handleEditSubmit} style={{ width: "100%" }}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title" id="editLeadSettingLabel">Edit Lead Auto-Close</h5>
                                    <button type="button" className="btn-close" id="LeadSettingsEditClose"
                                        data-bs-dismiss="modal" aria-label="Close" onClick={clearState} />
                                </div>
                                <div className="modal-body">
                                    <p className="text-muted" style={{ fontSize: 13 }}>
                                        Leads with no activity for the specified number of days will be automatically marked as <strong>Lost</strong>.
                                    </p>
                                    <div className="col-12 mb-3 d-flex align-items-center">
                                        <div className="col-md-5">
                                            <label className="form-label mb-0">Auto-Close After (Days)*</label>
                                        </div>
                                        <div className="col-md-7">
                                            <input
                                                type="number"
                                                className={`form-control ${error ? "is-invalid" : ""}`}
                                                placeholder="e.g. 30"
                                                value={autoCloseDays}
                                                min="1"
                                                onChange={(e) => { setAutoCloseDays(e.target.value); setError(""); }}
                                            />
                                            {error && <small className="text-danger">{error}</small>}
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-light waves-effect"
                                        data-bs-dismiss="modal" onClick={clearState}>Cancel</button>
                                    <button type="submit" className="btn btn-primary waves-effect waves-light"
                                        disabled={submitting}>
                                        {submitting ? "Updating..." : "Update"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default LeadSettings;
